import { useEffect, useRef, useState } from 'react'
import { DEFAULT_SCALE_SETTINGS } from './graphConstants'
import type { AnimationSettings, GraphPoint, TimestampPosition } from './types'

const getInterpolatedXFromTimestampPositions = ({
  timestamp,
  timestampPositions,
  padding,
  innerWidth,
}: {
  timestamp: number
  timestampPositions: TimestampPosition[]
  padding: { left: number }
  innerWidth: number
}) => {
  if (timestampPositions.length === 0) return padding.left + (innerWidth / 2)
  if (timestampPositions.length === 1) return timestampPositions[0].x
  if (timestamp <= timestampPositions[0].timestamp) return timestampPositions[0].x
  const lastTimestampPosition = timestampPositions[timestampPositions.length - 1]
  if (timestamp >= lastTimestampPosition.timestamp) return lastTimestampPosition.x
  for (let index = 1; index < timestampPositions.length; index++) {
    const previousTimestampPosition = timestampPositions[index - 1]
    const nextTimestampPosition = timestampPositions[index]
    if (timestamp > nextTimestampPosition.timestamp) continue
    const timestampSpan = nextTimestampPosition.timestamp - previousTimestampPosition.timestamp
    const progressWithinSpan = (timestamp - previousTimestampPosition.timestamp) / timestampSpan
    return previousTimestampPosition.x + (
      (nextTimestampPosition.x - previousTimestampPosition.x) * progressWithinSpan
    )
  }
  return lastTimestampPosition.x
}

const canInterpolateGraphPoints = (
  sourceGraphPoints: GraphPoint[],
  targetGraphPoints: GraphPoint[]
) =>
  sourceGraphPoints.length === targetGraphPoints.length &&
  sourceGraphPoints.every((sourceGraphPoint, index) =>
    sourceGraphPoint.date === targetGraphPoints[index].date &&
    sourceGraphPoint.timestamp === targetGraphPoints[index].timestamp
  )

// Interpolated value of a series at `timestamp`, clamped to the edge values
// outside its range (a shorter trajectory extends flat while morphing).
const getSeriesValueAtTimestamp = (
  points: GraphPoint[],
  timestamp: number,
): number => {
  if (points.length === 0) return 0
  if (timestamp <= points[0].timestamp) return points[0].value
  const lastPoint = points[points.length - 1]
  if (timestamp >= lastPoint.timestamp) return lastPoint.value
  for (let index = 1; index < points.length; index += 1) {
    if (timestamp > points[index].timestamp) continue
    const previousPoint = points[index - 1]
    const span = points[index].timestamp - previousPoint.timestamp
    const progressWithinSpan = span > 0 ? (timestamp - previousPoint.timestamp) / span : 0
    return previousPoint.value
      + (points[index].value - previousPoint.value) * progressWithinSpan
  }
  return lastPoint.value
}

// Resamples both series onto the union of their timestamps so that curves on
// DIFFERENT date grids (e.g. two plans' trajectories) can morph into each
// other — index-wise interpolation alone requires identical grids, and the
// previous behavior was to snap. The union points that aren't in the real
// target are dropped when the animation completes; by then the viewport has
// slid their anchors into (or off) the new layout, so nothing visibly pops.
const resampleToUnionTimestamps = (
  startGraphPoints: GraphPoint[],
  targetGraphPoints: GraphPoint[],
): { start: GraphPoint[]; target: GraphPoint[] } => {
  const unionTimestamps = Array.from(new Set([
    ...startGraphPoints.map(point => point.timestamp),
    ...targetGraphPoints.map(point => point.timestamp),
  ])).sort((a, b) => a - b)
  const dateByTimestamp = new Map<number, string>()
  for (const point of [...startGraphPoints, ...targetGraphPoints]) {
    if (!dateByTimestamp.has(point.timestamp)) {
      dateByTimestamp.set(point.timestamp, point.date)
    }
  }
  // Union points that the real target doesn't have exist only for the morph:
  // keep them out of markers/axis anchors (see GraphPoint.isMorphFiller).
  const realTargetTimestamps = new Set(targetGraphPoints.map(point => point.timestamp))
  const resampleSeries = (source: GraphPoint[]): GraphPoint[] =>
    unionTimestamps.map(timestamp => ({
      date: dateByTimestamp.get(timestamp)
        ?? new Date(timestamp).toISOString().slice(0, 10),
      timestamp,
      value: getSeriesValueAtTimestamp(source, timestamp),
      ...(realTargetTimestamps.has(timestamp) ? {} : { isMorphFiller: true }),
    }))
  return {
    start: resampleSeries(startGraphPoints),
    target: resampleSeries(targetGraphPoints),
  }
}

type TimedGraphPointAnimationOptions = {
  easing?: 'cubic' | 'sine'
  valueInterpolation?: 'linear' | 'logForLargeChanges'
}

const LARGE_VALUE_INTERPOLATION_RATIO = 8

const getLinearInterpolatedValue = (
  startValue: number,
  targetValue: number,
  progress: number,
) => startValue + ((targetValue - startValue) * progress)

const getInterpolatedGraphPointValue = ({
  startValue,
  targetValue,
  progress,
  valueInterpolation,
}: {
  startValue: number
  targetValue: number
  progress: number
  valueInterpolation: TimedGraphPointAnimationOptions['valueInterpolation']
}) => {
  if (
    valueInterpolation !== 'logForLargeChanges' ||
    startValue <= 0 ||
    targetValue <= 0
  ) {
    return getLinearInterpolatedValue(startValue, targetValue, progress)
  }

  const smallerValue = Math.min(startValue, targetValue)
  const largerValue = Math.max(startValue, targetValue)
  if (largerValue / smallerValue < LARGE_VALUE_INTERPOLATION_RATIO) {
    return getLinearInterpolatedValue(startValue, targetValue, progress)
  }

  const startLogValue = Math.log(startValue)
  const targetLogValue = Math.log(targetValue)
  return Math.exp(
    startLogValue + ((targetLogValue - startLogValue) * progress)
  )
}

const getNormalInterpolatedGraphPoints = (
  startGraphPoints: GraphPoint[],
  targetGraphPoints: GraphPoint[],
  progress: number,
  valueInterpolation: TimedGraphPointAnimationOptions['valueInterpolation'],
): GraphPoint[] =>
  startGraphPoints.map((startGraphPoint, index) => {
    const targetGraphPoint = targetGraphPoints[index]
    return {
      ...targetGraphPoint,
      value: getInterpolatedGraphPointValue({
        startValue: startGraphPoint.value,
        targetValue: targetGraphPoint.value,
        progress,
        valueInterpolation,
      }),
    }
  })

/** Maps linear time 0..1 to eased progress for useTimedAnimatedGraphPoints. */
export const easeInOutCubic = (t: number) =>
  t <= 0 ? 0 : t >= 1 ? 1 : t < 0.5 ? 4 * t * t * t : 1 - ((-2 * t + 2) ** 3) / 2

const easeInOutSine = (t: number) =>
  t <= 0 ? 0 : t >= 1 ? 1 : 0.5 - (Math.cos(Math.PI * t) / 2)

export const easeTimedGraphPointProgress = (
  linearProgress: number,
  easing: TimedGraphPointAnimationOptions['easing']
) => easing === 'sine'
  ? easeInOutSine(linearProgress)
  : easeInOutCubic(linearProgress)

export const usePlanBChinaLineRiseProgress = (
  enabled: boolean,
  durationMs: number,
) => {
  const [progress, setProgress] = useState(0)
  useEffect(() => {
    if (!enabled) {
      setProgress(0)
      return
    }
    let rafId = 0
    const start = performance.now()
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs)
      setProgress(t)
      if (t < 1) rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => {
      if (rafId !== 0) cancelAnimationFrame(rafId)
    }
  }, [enabled, durationMs])
  return progress
}

// NOTE for all the exponential-smoothing hooks below: the animation loop is
// PERSISTENT — it reads its target from a ref and keeps running across
// retargets instead of cancel+requestAnimationFrame on every target change.
// With ~8 instances animating during a HUD transition, the old restart-per-
// change pattern showed up as hundreds of rAF schedule/cancel calls per
// second in profiles. Unmount cleanups must null the frame-id ref, or
// StrictMode's setup→cleanup→setup would permanently freeze the loop.

export const useAnimatedTimestamp = (
  targetTimestamp: number,
  animationSettings: AnimationSettings
) => {
  const animatedRef = useRef(targetTimestamp)
  const targetRef = useRef(targetTimestamp)
  const settingsRef = useRef(animationSettings)
  settingsRef.current = animationSettings
  const [displayTimestamp, setDisplayTimestamp] = useState(targetTimestamp)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    targetRef.current = targetTimestamp
    if (rafRef.current !== null) return // loop already running

    const animate = () => {
      const diff = targetRef.current - animatedRef.current
      if (Math.abs(diff) < settingsRef.current.snapThresholdMs) {
        animatedRef.current = targetRef.current
        rafRef.current = null
        setDisplayTimestamp(targetRef.current)
        return
      }
      animatedRef.current += diff * settingsRef.current.smoothingFactor
      setDisplayTimestamp(animatedRef.current)
      rafRef.current = requestAnimationFrame(animate)
    }
    rafRef.current = requestAnimationFrame(animate)
  }, [targetTimestamp])

  useEffect(() => () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  return displayTimestamp
}

export const useAnimatedNumber = (
  targetValue: number,
  animationSettings: AnimationSettings,
  snapThreshold: number,
  enabled = true
) => {
  const animatedRef = useRef(targetValue)
  const targetRef = useRef(targetValue)
  const settingsRef = useRef({ animationSettings, snapThreshold })
  settingsRef.current = { animationSettings, snapThreshold }
  const [displayValue, setDisplayValue] = useState(targetValue)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    targetRef.current = targetValue
    if (!enabled) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      animatedRef.current = targetValue
      setDisplayValue(targetValue)
      return
    }
    if (rafRef.current !== null) return // loop already running

    const animate = () => {
      const diff = targetRef.current - animatedRef.current
      if (Math.abs(diff) < settingsRef.current.snapThreshold) {
        animatedRef.current = targetRef.current
        rafRef.current = null
        setDisplayValue(targetRef.current)
        return
      }
      animatedRef.current += diff * settingsRef.current.animationSettings.smoothingFactor
      setDisplayValue(animatedRef.current)
      rafRef.current = requestAnimationFrame(animate)
    }
    rafRef.current = requestAnimationFrame(animate)
  }, [targetValue, enabled])

  useEffect(() => () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  return enabled ? displayValue : targetValue
}

// 0..1 opacity that tweens linearly toward `visible` in a FIXED duration —
// unlike useAnimatedNumber's exponential smoothing, which never lands on a
// specific timing. A mid-fade flip reverses from the current value at the
// same rate, so rapid show/hide can't jump.
export const useFadeInOut = (visible: boolean, durationMs: number) => {
  const [displayOpacity, setDisplayOpacity] = useState(visible ? 1 : 0)
  const opacityRef = useRef(displayOpacity)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const target = visible ? 1 : 0
    if (opacityRef.current === target) return
    let lastFrameTime: number | null = null
    const step = (frameTime: number) => {
      const elapsed = lastFrameTime === null ? 0 : frameTime - lastFrameTime
      lastFrameTime = frameTime
      const delta = elapsed / durationMs
      const next = target > opacityRef.current
        ? Math.min(target, opacityRef.current + delta)
        : Math.max(target, opacityRef.current - delta)
      opacityRef.current = next
      setDisplayOpacity(next)
      rafRef.current = next === target ? null : requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [visible, durationMs])

  return displayOpacity
}

export const useAnimatedGraphPoints = (
  targetGraphPoints: GraphPoint[],
  animationSettings: AnimationSettings,
  seedGraphPoints?: GraphPoint[],
  seedKey?: string
) => {
  const animatedRef = useRef(targetGraphPoints)
  const targetRef = useRef(targetGraphPoints)
  const settingsRef = useRef(animationSettings)
  settingsRef.current = animationSettings
  const [displayGraphPoints, setDisplayGraphPoints] = useState(targetGraphPoints)
  const rafRef = useRef<number | null>(null)
  const lastSeedKeyRef = useRef(seedKey)

  useEffect(() => {
    if (
      seedKey &&
      seedKey !== lastSeedKeyRef.current &&
      seedGraphPoints &&
      canInterpolateGraphPoints(seedGraphPoints, targetGraphPoints)
    ) {
      animatedRef.current = seedGraphPoints
      setDisplayGraphPoints(seedGraphPoints)
    }
    lastSeedKeyRef.current = seedKey
    if (!canInterpolateGraphPoints(animatedRef.current, targetGraphPoints)) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      animatedRef.current = targetGraphPoints
      targetRef.current = targetGraphPoints
      setDisplayGraphPoints(targetGraphPoints)
      return
    }
    targetRef.current = targetGraphPoints
    if (rafRef.current !== null) return // loop already running

    const animate = () => {
      let shouldContinueAnimating = false
      const currentTarget = targetRef.current
      const nextGraphPoints = animatedRef.current.map((animatedGraphPoint, index) => {
        const targetGraphPoint = currentTarget[index]
        const diff = targetGraphPoint.value - animatedGraphPoint.value
        if (Math.abs(diff) < 0.01) return targetGraphPoint
        shouldContinueAnimating = true
        return {
          ...targetGraphPoint,
          value: animatedGraphPoint.value + (diff * settingsRef.current.smoothingFactor),
        }
      })

      animatedRef.current = nextGraphPoints
      setDisplayGraphPoints(nextGraphPoints)
      if (!shouldContinueAnimating) {
        animatedRef.current = currentTarget
        rafRef.current = null
        setDisplayGraphPoints(currentTarget)
        return
      }
      rafRef.current = requestAnimationFrame(animate)
    }
    rafRef.current = requestAnimationFrame(animate)
  }, [targetGraphPoints, seedGraphPoints, seedKey])

  useEffect(() => () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  return displayGraphPoints
}

export const useTimedAnimatedGraphPoints = (
  targetGraphPoints: GraphPoint[],
  durationMs: number,
  seedGraphPoints?: GraphPoint[],
  seedKey?: string,
  options: TimedGraphPointAnimationOptions = {}
) => {
  const animatedRef = useRef(targetGraphPoints)
  const [displayGraphPoints, setDisplayGraphPoints] = useState(targetGraphPoints)
  const rafRef = useRef<number | null>(null)
  const lastSeedKeyRef = useRef<string | undefined>(undefined)
  const seedGraphPointsRef = useRef(seedGraphPoints)
  seedGraphPointsRef.current = seedGraphPoints

  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    const seedSnapshot = seedGraphPointsRef.current
    if (
      seedKey &&
      seedKey !== lastSeedKeyRef.current &&
      seedSnapshot &&
      canInterpolateGraphPoints(seedSnapshot, targetGraphPoints)
    ) {
      animatedRef.current = seedSnapshot
      setDisplayGraphPoints(seedSnapshot)
    }
    lastSeedKeyRef.current = seedKey
    const startGraphPoints = animatedRef.current
    if (durationMs <= 0) {
      animatedRef.current = targetGraphPoints
      setDisplayGraphPoints(targetGraphPoints)
      return
    }
    // Same date grid → interpolate index-wise as-is. Different grids (a plan
    // switch) → resample both onto the union grid and morph between the two
    // trajectories. Only degenerate series (empty/single-point, e.g. a line
    // toggling into existence) still snap.
    let animationStartPoints = startGraphPoints
    let animationTargetPoints = targetGraphPoints
    if (!canInterpolateGraphPoints(startGraphPoints, targetGraphPoints)) {
      if (startGraphPoints.length < 2 || targetGraphPoints.length < 2) {
        animatedRef.current = targetGraphPoints
        setDisplayGraphPoints(targetGraphPoints)
        return
      }
      const resampled = resampleToUnionTimestamps(startGraphPoints, targetGraphPoints)
      animationStartPoints = resampled.start
      animationTargetPoints = resampled.target
    }
    const startTime = performance.now()
    const easing = options.easing
    const valueInterpolation = options.valueInterpolation

    const animate = (currentTime: number) => {
      const linearProgress = Math.min(1, (currentTime - startTime) / durationMs)
      const progress = easeTimedGraphPointProgress(linearProgress, easing)
      const nextGraphPoints = getNormalInterpolatedGraphPoints(
        animationStartPoints,
        animationTargetPoints,
        progress,
        valueInterpolation,
      )

      animatedRef.current = nextGraphPoints
      setDisplayGraphPoints(nextGraphPoints)
      if (linearProgress < 1) {
        rafRef.current = requestAnimationFrame(animate)
        return
      }
      animatedRef.current = targetGraphPoints
      setDisplayGraphPoints(targetGraphPoints)
    }

    rafRef.current = requestAnimationFrame(animate)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [
    targetGraphPoints,
    durationMs,
    seedKey,
    options.easing,
    options.valueInterpolation,
  ])

  return displayGraphPoints
}

const computeGhostOffCanvasX = ({
  targetPositions,
  droppedTimestamp,
}: {
  targetPositions: TimestampPosition[]
  droppedTimestamp: number
}): number => {
  if (targetPositions.length === 0) return 0
  if (targetPositions.length === 1) return targetPositions[0].x
  const lastIndex = targetPositions.length - 1
  const spacing = (targetPositions[lastIndex].x - targetPositions[0].x) / lastIndex
  if (droppedTimestamp >= targetPositions[lastIndex].timestamp) {
    return targetPositions[lastIndex].x + spacing
  }
  if (droppedTimestamp <= targetPositions[0].timestamp) {
    return targetPositions[0].x - spacing
  }
  // Dropped timestamp INSIDE the new range (e.g. a quarterly point between
  // surviving yearly anchors after a plan switch): sending it toward an edge
  // would drag its anchor — and every line point at that timestamp — across
  // the plot mid-animation, folding the capability line into spaghetti
  // (anchor x must stay monotonic in timestamp). Settle it at the x where
  // its timestamp falls in the new layout instead: it stays collinear with
  // its neighbors, invisibly, until it stops being an anchor.
  return getInterpolatedXFromTimestampPositions({
    timestamp: droppedTimestamp,
    timestampPositions: targetPositions,
    padding: DEFAULT_SCALE_SETTINGS.padding,
    innerWidth: 0,
  })
}

// Spring integration shared by the viewport anchors/y-domain below and
// matching useAnimatedDate's motion: critically damped, settling in
// ≈durationMs (step response reaches ~99% at ω·t ≈ 6.64), sub-stepped so a
// dropped frame integrates as several stable small steps.
const SPRING_SETTLE_OMEGA_FACTOR = 6.64
const SPRING_MAX_STEP_S = 1 / 30

type SpringState = { position: number; velocity: number }

const stepSpring = (
  state: SpringState,
  target: number,
  omegaPerS: number,
  frameSeconds: number,
) => {
  let remainingSeconds = frameSeconds
  while (remainingSeconds > 0) {
    const stepSeconds = Math.min(remainingSeconds, SPRING_MAX_STEP_S)
    remainingSeconds -= stepSeconds
    const displacement = state.position - target
    const acceleration =
      -omegaPerS * omegaPerS * displacement - 2 * omegaPerS * state.velocity
    state.velocity += acceleration * stepSeconds
    state.position += state.velocity * stepSeconds
  }
}

export const useTimedGraphViewport = ({
  targetYDomainMax,
  targetTimestampPositions,
  durationMs,
  ySnapThreshold = 0.01,
}: {
  targetYDomainMax: number
  targetTimestampPositions: TimestampPosition[]
  durationMs: number
  ySnapThreshold?: number
}) => {
  // Anchors and the y-domain move as critically damped springs (per-anchor
  // state keyed by timestamp, velocity carried across retargets) instead of
  // restarting a fixed-duration tween on every target change. During a
  // transition the visible point set can change several times (points appear
  // scrolling backward, drop to ghosts scrolling forward); a restarting tween
  // gave each anchor a velocity discontinuity at every one of those moments —
  // visible as mid-transition lurches of the capability line — while springs
  // stay C1-continuous through any retarget, forwards and backwards.
  const anchorStatesRef = useRef(new Map<number, SpringState>())
  const yStateRef = useRef<SpringState>({ position: targetYDomainMax, velocity: 0 })
  const springTargetsRef = useRef({
    augmented: targetTimestampPositions,
    rawTargets: targetTimestampPositions,
    yTarget: targetYDomainMax,
    omegaPerS: SPRING_SETTLE_OMEGA_FACTOR / (Math.max(1, durationMs) / 1000),
    ySnapThreshold,
  })
  const [displayViewport, setDisplayViewport] = useState({
    yDomainMax: targetYDomainMax,
    timestampPositions: targetTimestampPositions,
  })
  const rafRef = useRef<number | null>(null)
  const lastFrameTimeRef = useRef<number | null>(null)

  useEffect(() => {
    if (targetTimestampPositions.length === 0) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      lastFrameTimeRef.current = null
      anchorStatesRef.current.clear()
      yStateRef.current = { position: targetYDomainMax, velocity: 0 }
      setDisplayViewport({
        yDomainMax: targetYDomainMax,
        timestampPositions: [],
      })
      return
    }

    // Current animated layout, for seeding anchors that just (re)appeared.
    const currentLayout = Array.from(anchorStatesRef.current.entries())
      .map(([timestamp, state]) => ({ timestamp, x: state.position }))
      .sort((a, b) => a.timestamp - b.timestamp)
    const newTargetTimestamps = new Set(targetTimestampPositions.map(p => p.timestamp))
    const ghostTargetEntries = currentLayout
      .filter(position => !newTargetTimestamps.has(position.timestamp))
      .map(position => ({
        timestamp: position.timestamp,
        x: computeGhostOffCanvasX({
          targetPositions: targetTimestampPositions,
          droppedTimestamp: position.timestamp,
        }),
      }))
    const augmented = [...targetTimestampPositions, ...ghostTargetEntries]
      .sort((entryA, entryB) => entryA.timestamp - entryB.timestamp)
    const augmentedTimestamps = new Set(augmented.map(entry => entry.timestamp))
    for (const entry of augmented) {
      if (anchorStatesRef.current.has(entry.timestamp)) continue
      anchorStatesRef.current.set(entry.timestamp, {
        // A new anchor starts where its timestamp falls in the CURRENT
        // animated layout (so it appears in place and glides), not at its
        // final position.
        position: currentLayout.length >= 2
          ? getInterpolatedXFromTimestampPositions({
            timestamp: entry.timestamp,
            timestampPositions: currentLayout,
            padding: DEFAULT_SCALE_SETTINGS.padding,
            innerWidth: 0,
          })
          : entry.x,
        velocity: 0,
      })
    }
    for (const timestamp of Array.from(anchorStatesRef.current.keys())) {
      if (!augmentedTimestamps.has(timestamp)) {
        anchorStatesRef.current.delete(timestamp)
      }
    }
    springTargetsRef.current = {
      augmented,
      rawTargets: targetTimestampPositions,
      yTarget: targetYDomainMax,
      omegaPerS: SPRING_SETTLE_OMEGA_FACTOR / (Math.max(1, durationMs) / 1000),
      ySnapThreshold,
    }

    if (durationMs <= 0) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      lastFrameTimeRef.current = null
      anchorStatesRef.current = new Map(targetTimestampPositions.map(
        position => [position.timestamp, { position: position.x, velocity: 0 }]
      ))
      yStateRef.current = { position: targetYDomainMax, velocity: 0 }
      setDisplayViewport({
        yDomainMax: targetYDomainMax,
        timestampPositions: targetTimestampPositions,
      })
      return
    }

    if (rafRef.current !== null) return // spring loop already running

    const runSprings = (timestamp: number) => {
      const frameSeconds = lastFrameTimeRef.current === null
        ? 1 / 60
        : Math.min(0.25, (timestamp - lastFrameTimeRef.current) / 1000)
      lastFrameTimeRef.current = timestamp
      const targets = springTargetsRef.current

      let allSettled = true
      for (const entry of targets.augmented) {
        const anchorState = anchorStatesRef.current.get(entry.timestamp)
        if (!anchorState) continue
        stepSpring(anchorState, entry.x, targets.omegaPerS, frameSeconds)
        if (
          Math.abs(anchorState.position - entry.x) > 0.05
          || Math.abs(anchorState.velocity) > 0.5
        ) {
          allSettled = false
        }
      }
      const yState = yStateRef.current
      stepSpring(yState, targets.yTarget, targets.omegaPerS, frameSeconds)
      const ySettleEpsilon = Math.max(
        targets.ySnapThreshold,
        Math.abs(targets.yTarget) * 0.0005,
      )
      if (
        Math.abs(yState.position - targets.yTarget) > ySettleEpsilon
        || Math.abs(yState.velocity) > ySettleEpsilon * 10
      ) {
        allSettled = false
      }

      if (allSettled) {
        anchorStatesRef.current = new Map(targets.rawTargets.map(
          position => [position.timestamp, { position: position.x, velocity: 0 }]
        ))
        yStateRef.current = { position: targets.yTarget, velocity: 0 }
        lastFrameTimeRef.current = null
        rafRef.current = null
        setDisplayViewport({
          yDomainMax: targets.yTarget,
          timestampPositions: targets.rawTargets,
        })
        return
      }
      setDisplayViewport({
        yDomainMax: yState.position,
        timestampPositions: targets.augmented.map(entry => ({
          timestamp: entry.timestamp,
          x: anchorStatesRef.current.get(entry.timestamp)?.position ?? entry.x,
        })),
      })
      rafRef.current = requestAnimationFrame(runSprings)
    }
    rafRef.current = requestAnimationFrame(runSprings)
  }, [
    targetYDomainMax,
    targetTimestampPositions,
    durationMs,
    ySnapThreshold,
  ])

  // Unmount cleanup only: the spring loop survives retargets. Must ALSO null
  // the ref — under StrictMode's setup→cleanup→setup cycle a stale id would
  // otherwise convince the "loop already running" guard to never start a
  // spring again, freezing the viewport permanently.
  useEffect(() => () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
      lastFrameTimeRef.current = null
    }
  }, [])

  return displayViewport
}
